<template>
  <div aria-hidden="true" style="display: none"></div>
</template>

<script setup lang="ts">
import { bbox as turfBbox } from '@turf/turf'
import { ref, computed, watch, onUnmounted } from 'vue'
import type Map from '@/components/map/Map.vue'
import { useMyMapStore } from '@/stores/myMapStore'
import { CommuneLayerService } from '@/services/map/CommuneLayerService'
import { type CommuneData } from '@/views/map/components/CommuneCard.vue'

const props = defineProps<{
  myMap: InstanceType<typeof Map> | undefined
}>()

const myMapStore = useMyMapStore()
const communeData = ref<CommuneData | null>(null)
const map = computed(() => props.myMap?.map)

let hoveredFeatureId: string | number | undefined = undefined
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let clickHandler: ((e: any) => void) | null = null
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let mousemoveFillHandler: ((e: any) => void) | null = null
let mouseleaveFillHandler: (() => void) | null = null
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let mousemoveOutlineHandler: ((e: any) => void) | null = null
let mouseleaveOutlineHandler: (() => void) | null = null

/**
 * Initialise la couche des communes et configure les événements
 */
const initializeCommuneLayer = async () => {
  if (!props.myMap || !map.value) {
    return
  }

  // Ajouter la couche des communes
  await CommuneLayerService.addCommuneLayerToMap(props.myMap)

  const layerId = CommuneLayerService.getLayerId()
  const outlineLayerId = CommuneLayerService.getOutlineLayerId()

  if (!map.value) return

  // Handler de clic sur les communes
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  clickHandler = async (e: any) => {
    const clickStart = performance.now()
    if (e.features && e.features.length > 0) {
      const feature = e.features[0]
      const properties = feature.properties || {}
      const communeName =
        (properties.cmn_titre || properties.nom || properties.name || null) as string | null
      const featureId = feature.id ?? properties.cmn_code
      console.log('[CommuneLayerHandler] click', {
        commune: communeName,
        featureId,
        lngLat: e.lngLat ? [e.lngLat.lng, e.lngLat.lat] : undefined,
      })

      // Désactiver le hover immédiatement avant de sélectionner
      if (map.value && hoveredFeatureId !== undefined) {
        try {
          map.value.setFeatureState(
            {
              source: CommuneLayerService.getSourceId(),
              sourceLayer: 'commune',
              id: hoveredFeatureId,
            },
            { hover: false },
          )
        } catch { /* silently ignore */ }
        hoveredFeatureId = undefined
      }

      // Sélectionner la commune et obscurcir les autres
      if (map.value && featureId) {
        try {
          CommuneLayerService.selectFeature(map.value, featureId)
        } catch { /* silently ignore — prevents blocking fiche display */ }
      }

      // API UUID: feature.id after promoteId:'cmn_code' contains cmn_code, not UUID.
      // Use tile property fields that carry the actual UUID when present.
      const communeId =
        properties.id ||
        properties.cmn_id ||
        properties.commune_id ||
        properties.fk_ctn_id

      if (communeName) {
        const geometry = feature.geometry as GeoJSON.Polygon | GeoJSON.MultiPolygon
        if (geometry && map.value) {
          const [minLng, minLat, maxLng, maxLat] = turfBbox({
            type: 'Feature',
            properties: {},
            geometry,
          })
          map.value.fitBounds(
            [[minLng, minLat], [maxLng, maxLat]],
            { padding: 40, duration: 800 },
          )
        }
      }

      // Extraire les données de la commune depuis les propriétés de la feature
      communeData.value = {
        id: communeId != null ? String(communeId) : undefined,
        // Champs principaux de la BD
        cmn_code: properties.cmn_code,
        cmn_titre: properties.cmn_titre || properties.nom || properties.name || 'Commune',
        cmn_dept: properties.cmn_dept || properties.departement || properties.department,
        cmn_region: properties.cmn_region || properties.region,
        cmn_population:
          properties.cmn_population !== undefined ? Number(properties.cmn_population) : undefined,
        cmn_statut: properties.cmn_statut,
        cmn_boite_postale: properties.cmn_boite_postale || properties.adresse || properties.address,
        cmn_site_web: properties.cmn_site_web || properties.site || properties.website,
        cmn_superficie:
          properties.cmn_superficie !== undefined ? Number(properties.cmn_superficie) : undefined,
        cmn_densite: properties.cmn_densite || properties.densite || properties.density,
        cmn_email_marie: properties.cmn_email_marie || properties.couriel || properties.email,
        cmn_maire: properties.cmn_maire || properties.maire || properties.mayor,
        cmn_telephone: properties.cmn_telephone || properties.telephone || properties.phone,
        cmn_nbr_conseiller:
          properties.cmn_nbr_conseiller !== undefined
            ? Number(properties.cmn_nbr_conseiller)
            : undefined,
        cmn_nbr_conseiller_femme:
          properties.cmn_nbr_conseiller_femme !== undefined
            ? Number(properties.cmn_nbr_conseiller_femme)
            : undefined,
        cmn_nbr_adjoint:
          properties.cmn_nbr_adjoint !== undefined ? Number(properties.cmn_nbr_adjoint) : undefined,
        cmn_nbr_adjoint_femme:
          properties.cmn_nbr_adjoint_femme !== undefined
            ? Number(properties.cmn_nbr_adjoint_femme)
            : undefined,
        cmn_date_creation: properties.cmn_date_creation,
        // Champs de compatibilité (anciens noms)
        nom: properties.cmn_titre || properties.nom || properties.name || 'Commune',
        departement: properties.cmn_dept || properties.departement || properties.department,
        region: properties.cmn_region || properties.region,
        habitants:
          properties.cmn_population !== undefined ? Number(properties.cmn_population) : undefined,
        superficie:
          properties.cmn_superficie !== undefined ? Number(properties.cmn_superficie) : undefined,
        densite: properties.cmn_densite || properties.densite || properties.density,
      }

      myMapStore.setCommuneFicheFromCommuneLayer(communeData.value)
    }
    const clickEnd = performance.now()
    console.log(
      `[CommuneLayerHandler] Temps de traitement du clic: ${(clickEnd - clickStart).toFixed(2)}ms`,
    )
  }

  map.value.on('click', layerId, clickHandler)

  // Handler de hover pour la couche fill
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  mousemoveFillHandler = (e: any) => {
    if (map.value && e.features && e.features.length > 0) {
      const feature = e.features[0]
      const properties = feature.properties || {}
      const featureId = feature.id ?? properties.cmn_code

      if (featureId !== undefined && featureId !== null) {
        if (hoveredFeatureId !== featureId) {
          // Désélectionner la feature précédemment survolée
          if (hoveredFeatureId !== undefined && hoveredFeatureId !== null) {
            try {
              map.value.setFeatureState(
                {
                  source: CommuneLayerService.getSourceId(),
                  sourceLayer: 'commune',
                  id: hoveredFeatureId,
                },
                { hover: false },
              )
            } catch (error) {
              console.error('[CommuneLayerHandler] Error setting hover false:', error, {
                id: hoveredFeatureId,
              })
            }
          }

          // Mettre à jour la feature actuellement survolée
          hoveredFeatureId = featureId
          const name = (properties.cmn_titre || properties.nom || properties.name) as
            | string
            | undefined
          console.log('[CommuneLayerHandler] hover enter', { commune: name, featureId })
          try {
            map.value.setFeatureState(
              {
                source: CommuneLayerService.getSourceId(),
                sourceLayer: 'commune',
                id: hoveredFeatureId,
              },
              { hover: true },
            )
          } catch (error) {
            console.error('[CommuneLayerHandler] Error setting hover true:', error, {
              id: hoveredFeatureId,
            })
          }
        }

        map.value.getCanvas().style.cursor = 'pointer'
      }
    }
  }

  mouseleaveFillHandler = () => {
    if (map.value && hoveredFeatureId) {
      console.log('[CommuneLayerHandler] hover leave', { featureId: hoveredFeatureId })
      map.value.setFeatureState(
        {
          source: CommuneLayerService.getSourceId(),
          sourceLayer: 'commune',
          id: hoveredFeatureId,
        },
        { hover: false },
      )
      hoveredFeatureId = undefined
    }
    if (map.value) {
      map.value.getCanvas().style.cursor = ''
    }
  }

  map.value.on('mousemove', layerId, mousemoveFillHandler)
  map.value.on('mouseleave', layerId, mouseleaveFillHandler)

  // Handler de hover pour la couche outline
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  mousemoveOutlineHandler = (e: any) => {
    if (map.value && e.features && e.features.length > 0) {
      const feature = e.features[0]
      const properties = feature.properties || {}
      const featureId = feature.id ?? properties.cmn_code

      if (featureId !== undefined && featureId !== null) {
        if (hoveredFeatureId !== featureId) {
          // Désélectionner la feature précédemment survolée
          if (hoveredFeatureId !== undefined && hoveredFeatureId !== null) {
            map.value.setFeatureState(
              {
                source: CommuneLayerService.getSourceId(),
                sourceLayer: 'commune',
                id: hoveredFeatureId,
              },
              { hover: false },
            )
          }

          // Mettre à jour la feature actuellement survolée
          hoveredFeatureId = featureId
          const name = (properties.cmn_titre || properties.nom || properties.name) as
            | string
            | undefined
          console.log('[CommuneLayerHandler] hover enter (outline)', { commune: name, featureId })
          map.value.setFeatureState(
            {
              source: CommuneLayerService.getSourceId(),
              sourceLayer: 'commune',
              id: hoveredFeatureId,
            },
            { hover: true },
          )
        }

        map.value.getCanvas().style.cursor = 'pointer'
      }
    }
  }

  mouseleaveOutlineHandler = () => {
    if (map.value && hoveredFeatureId) {
      console.log('[CommuneLayerHandler] hover leave (outline)', { featureId: hoveredFeatureId })
      map.value.setFeatureState(
        {
          source: CommuneLayerService.getSourceId(),
          sourceLayer: 'commune',
          id: hoveredFeatureId,
        },
        { hover: false },
      )
      hoveredFeatureId = undefined
    }
    if (map.value) {
      map.value.getCanvas().style.cursor = ''
    }
  }

  map.value.on('mousemove', outlineLayerId, mousemoveOutlineHandler)
  map.value.on('mouseleave', outlineLayerId, mouseleaveOutlineHandler)
}

/**
 * Nettoie les événements de la carte
 */
const cleanup = () => {
  if (!map.value) return

  const layerId = CommuneLayerService.getLayerId()
  const outlineLayerId = CommuneLayerService.getOutlineLayerId()

  if (clickHandler) {
    map.value.off('click', layerId, clickHandler)
    clickHandler = null
  }

  if (mousemoveFillHandler) {
    map.value.off('mousemove', layerId, mousemoveFillHandler)
    mousemoveFillHandler = null
  }

  if (mouseleaveFillHandler) {
    map.value.off('mouseleave', layerId, mouseleaveFillHandler)
    mouseleaveFillHandler = null
  }

  if (mousemoveOutlineHandler) {
    map.value.off('mousemove', outlineLayerId, mousemoveOutlineHandler)
    mousemoveOutlineHandler = null
  }

  if (mouseleaveOutlineHandler) {
    map.value.off('mouseleave', outlineLayerId, mouseleaveOutlineHandler)
    mouseleaveOutlineHandler = null
  }
}

// Initialiser quand la carte est disponible
watch(
  () => map.value,
  (newMap) => {
    if (newMap) {
      initializeCommuneLayer()
    }
  },
  { immediate: true },
)

// Nettoyer lors du démontage
onUnmounted(() => {
  cleanup()
})

defineExpose({ communeData })
</script>
